/**
 * What an uploaded workbook can be. One entry per source, mirrored by an IFileImportProfile on
 * the server (same ids). The server detects the source from the file itself, so nothing here is
 * chosen up front: the Upload step shows the default source's sheet setup, and every other source
 * is a disclosure under it ("Exporting from LCvista?") that opens that tool's export walkthrough.
 * The review line and the Recent imports list name the source the server detected.
 *
 * Adding a tool (Prolaera, a course provider's transcript) is one entry here and one profile class
 * on the server; it appears as another disclosure, never as a new step or route.
 *
 * A tool stays `enabled: false` until a real export has been run through the parser (the server side
 * is the matching `CreditImports:<Tool>:Enabled` setting). Disabled tools are not listed, get no
 * "read as" line and no Recent imports badge: a batch the server tagged with one reads as a plain
 * spreadsheet.
 */

export type FileImportSourceId = 'template' | 'lcvista';

export interface FileImportSource {
  id: FileImportSourceId;
  /** Short name: the "read as" line, the Recent imports badge, and the disclosure label ("Exporting from LCvista?"). */
  name: string;
  /** Default source only: heading of the sheet-setup section on the Upload step. */
  heading?: string;
  /** One line under the heading or at the top of the disclosure. */
  intro?: string;
  /** The export walkthrough, three steps at most. */
  steps?: string[];
  /** Anything the person should know before they upload. */
  note?: string;
  /** Firm mode only. */
  firmNote?: string;
  /** Offer our template to download. */
  template: boolean;
  /** Show the sheet rules (layout, dates, hours). */
  rules: boolean;
  /** Review step: "read as {readAs}". */
  readAs: string;
  /** Listed on the Upload step and named in the review. Off until a real export has verified the profile. */
  enabled: boolean;
}

export const FILE_IMPORT_SOURCES: readonly FileImportSource[] = [
  {
    // The default and the catch-all: our template, or any .xlsx with one row per activity (exports from
    // tools we have no profile for land here; the parser reads them the same way, and unrecognized
    // columns are matched in the review). A tool gets its own entry only when it brings different behaviour.
    id: 'template',
    name: 'Spreadsheet',
    heading: 'Setting up your spreadsheet',
    firmNote: 'Each row needs the Email of someone who has signed in to this firm.',
    template: true,
    rules: true,
    readAs: 'a spreadsheet',
    enabled: true,
  },
  {
    // Hidden 2026-10-19: the profile was built from LCvista help-center screenshots and no account exists to
    // verify it against a real Compliance Detail export. Flip this (and CreditImports:LcVista:Enabled) once one has.
    id: 'lcvista',
    name: 'LCvista',
    steps: [
      'Open Compliance Detail (not Transcript).',
      'Export to Excel, not CSV.',
      'Upload it here unchanged.',
    ],
    note: 'Topic areas are matched to fields of study in the review.',
    firmNote: 'One export per person; the email comes from its name line.',
    template: false,
    rules: false,
    readAs: 'an LCvista export',
    enabled: false,
  },
];

export const DEFAULT_FILE_IMPORT_SOURCE: FileImportSourceId = 'template';

/** The source for an id; anything unknown (the server's "other" fallback, old drafts) or disabled reads as a plain spreadsheet. */
export function fileImportSource(id: string | null | undefined): FileImportSource {
  return FILE_IMPORT_SOURCES.find((s) => s.id === id && s.enabled) ?? FILE_IMPORT_SOURCES[0];
}

/** The default source's entry: the sheet setup shown on every Upload step. */
export function defaultFileImportSource(): FileImportSource {
  return fileImportSource(DEFAULT_FILE_IMPORT_SOURCE);
}

/** Every enabled source but the default, in registry order: each one is a disclosure on the Upload step. */
export function toolFileImportSources(): readonly FileImportSource[] {
  return FILE_IMPORT_SOURCES.filter((s) => s.id !== DEFAULT_FILE_IMPORT_SOURCE && s.enabled);
}

export function isFileImportSourceId(value: string | null | undefined): value is FileImportSourceId {
  return FILE_IMPORT_SOURCES.some((s) => s.id === value);
}

/** Badge text for a batch in Recent imports; plain spreadsheets (our template, a source we never profiled, or a disabled tool) need none. */
export function fileImportSourceLabel(id: string | null | undefined): string | null {
  const source = FILE_IMPORT_SOURCES.find((s) => s.id === id && s.enabled);
  return !source || source.id === DEFAULT_FILE_IMPORT_SOURCE ? null : source.name;
}
